"use client"

import { Fragment, useState, type ReactNode } from "react"

import type { CasinoProviderDTO } from "@/domain/casino/list-lobby-facets"
import { SearchInput } from "@/features/home/Search"
import {
  CasinoIcon,
  LiveIcon,
  OriginalsIcon,
  SlotsIcon,
  StarIcon,
  type IconComponent
} from "@/src/shared/assets/icons"

import { CategoryChips } from "./components/CategoryChips"
import { GamesFilteredGrid } from "./components/GamesFilteredGrid"
import { GamesPreviewRow } from "./components/GamesPreviewRow"
import { LobbyFilters } from "./components/LobbyFilters"
import { ProviderRail } from "./components/ProviderRail"
import {
  casinoSeeAllHref,
  defaultLobbyChip,
  mockFilteredGames,
  mockRailGames,
  railForChip,
  railsForChip,
  type LobbyChipId,
  type LobbyRailId
} from "./data/lobby"
import { lobbyTexts } from "./localization/texts"

const railIcons: Record<LobbyRailId, { Icon: IconComponent; viewBox: string }> =
  {
    originals: { Icon: OriginalsIcon, viewBox: "0 0 19 20" },
    winningNow: { Icon: StarIcon, viewBox: "0 0 16 24" },
    slots: { Icon: SlotsIcon, viewBox: "0 0 18 18" },
    blackjack: { Icon: CasinoIcon, viewBox: "0 0 24 24" },
    live: { Icon: LiveIcon, viewBox: "0 0 24 24" }
  }

function LobbyGameRail({ id }: { id: LobbyRailId }) {
  const { Icon, viewBox } = railIcons[id]

  return (
    <GamesPreviewRow
      title={lobbyTexts.rails[id]}
      href={casinoSeeAllHref}
      games={mockRailGames[id]}
      categorySelected={id}
      icon={
        <Icon
          viewBox={viewBox}
          className="block size-5 shrink-0 self-center overflow-visible [&_path]:[fill-opacity:1]"
          aria-hidden
        />
      }
    />
  )
}

export type LobbyProps = {
  promo: ReactNode
  providers: CasinoProviderDTO[]
}

export function Lobby({ promo, providers }: LobbyProps) {
  const [selectedChip, setSelectedChip] = useState<LobbyChipId | null>(
    defaultLobbyChip
  )

  return (
    <div
      className="mt-[15px] overflow-x-hidden"
      aria-label={lobbyTexts.ariaLabel}
    >
      <div className="flex flex-col gap-2.5">
        {promo}
        <div className="md:hidden">
          <SearchInput
            placeholder={lobbyTexts.searchPlaceholder}
            variant="default"
          />
        </div>
        <CategoryChips selected={selectedChip} onSelect={setSelectedChip} />
        <LobbyFilters providers={providers} />
        <div className="mt-2.5 flex min-w-0 flex-col gap-5">
          {selectedChip ? (
            <GamesFilteredGrid
              key={selectedChip}
              games={mockFilteredGames(railForChip(selectedChip))}
              categorySelected={railForChip(selectedChip)}
            />
          ) : (
            railsForChip(null).map((id) => {
              if (id !== "slots") {
                return <LobbyGameRail key={id} id={id} />
              }

              return (
                <Fragment key={id}>
                  <LobbyGameRail id={id} />
                  <ProviderRail key="provider-rail" providers={providers} />
                </Fragment>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
